import React, { useState, useEffect } from 'react';
import { Alert, Button, Form, Modal, ListGroup,Dropdown, InputGroup } from 'react-bootstrap';
import { DatePicker } from "../datePicker";
import { displayToastMsg } from '../toastMessages/toastmessages';
import { getDataReader, RPWDataReader } from '../../livedataFuncs';
import { lddGlobalSessionInfo } from '../navigationTypes';
import { lddStateObject } from '../../redux/reducer';
import { useAppDispatch } from '../../redux/hooks';
import { setCalendarAppointment } from '../../redux/reducer';
import axios from 'axios';
import { Logger } from '../../../../share/utils/logger';

interface AddApptModalProps {
    showAddModal: boolean;
    setShowAddModal: (show: boolean) => void;
    handleAddAppointment: (newAppointment: any) => void;
    livedataRootContainer?: lddStateObject['currentNavigation']['livedataRootContainer'];
    SESSION_INFO: lddGlobalSessionInfo;
    isFromCalendar: boolean;
}

const AddNewAppModal: React.FC<AddApptModalProps> = ({
    showAddModal,
    setShowAddModal,
    handleAddAppointment,
    livedataRootContainer,
    SESSION_INFO,
    isFromCalendar = false
}) => {
    const [appTitle, setAppTitle] = useState<string>("");
    const [appDetails, setAppDetails] = useState<string>("");
    const [appDate, setAppDate] = useState<string>("");
    const [appTime, setAppTime] = useState<string>("");
    const [hour, setHour] = useState<string>("");
    const [minute, setMinute] = useState<string>("00");
    const [amPm, setAmPm] = useState<string>("AM");
    const dispatch = useAppDispatch();
    const [searchTerm, setSearchTerm] = useState("");
    const [showDropdown, setShowDropdown] = useState<boolean>(false);
    const [firmFiles, setFirmFiles] = useState<any[]>([]);
    const [isLoadingFiles, setIsLoadingFiles] = useState<boolean>(false);
    const [selectedFile, setSelectedFile] = useState<any>(null);

    //code for link to current file
    //const [linkToCurrentFile, setLinkToCurrentFile] = useState<boolean>(false); 
    const [validationError, setValidationError] = useState<string | null>(null);
    
    const fetchFirmFiles = async () => {
        if (!SESSION_INFO?.firm?.id) {
            return;
        }
        setIsLoadingFiles(true);
        try {
            const queryParams = new URLSearchParams({
                LawyerID: SESSION_INFO.firm.id.toString(),
                SessionID: SESSION_INFO.sid
            }).toString();

            const response = await axios.get(`/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${SESSION_INFO.appProvinceShort}/${SESSION_INFO.firm.id}/getfilebylawyer?${queryParams}`);
            setFirmFiles(response.data.result);
        } catch (error) {
            Logger.error('Error fetching firm files:', error);
        } finally {
            setIsLoadingFiles(false);
        }
    };

    const filtteredFiles = firmFiles.filter((file: any) => {
        const searchText = searchTerm.toLowerCase();
        return file.MatterNumber
            ?.toLowerCase()
            .includes(searchText.toLowerCase()) ?? false;
    });

    const getCurrentFileInfo = () => {
        if (!livedataRootContainer?.dataRoot) {
            return { appId: null, matterNumber: null };
        }
        try {
            const DataReaderObj: RPWDataReader = getDataReader(livedataRootContainer.dataRoot);
            const appId = DataReaderObj?.getAppId();
            const matterNumber = DataReaderObj?.getFileNumber();
            return { appId, matterNumber };
        } catch (error) {
            Logger.error('Error getting current file info:', error);
            return { appId: null, matterNumber: null };
        }
    };
    
    const formatTimeString = () => {
        if (!hour || !minute) return "";
        const formattedHour = hour.padStart(2, '0');
        return `${formattedHour}:${minute.padStart(2, '0')} ${amPm}`;
    };

    const extractTitleFromShortDescr = (shortDescr: string): string => {
        if (!shortDescr) return "";
        const timePattern = /\s*-\s*\d{1,2}:\d{1,2}\s*(AM|PM)$/i;
        return shortDescr.replace(timePattern, "").trim();
    };


    const buildNewShortDescr = (title: string, time: string, matterNumber?: string): string => {
        let shrtDesc = '';
        if (title) {
            shrtDesc = title;
        }
        if (matterNumber) {
            shrtDesc += ' - ' + matterNumber;
        }
        if (time) {
            shrtDesc += ' - ' + time;
        }
        return shrtDesc;
    };

    const buildNewLongDescr = (title: string, time: string, matterNumber?: string): string => {
        let longDesc = '';
        if (time) {
            longDesc = time + ' - ';
        }
        if (title) {
            longDesc += title;
        }
        if (matterNumber) {
            longDesc += ' - ' + matterNumber;
        }
        return longDesc;
    };

    const handleFileSelect = (file: any) => {
        setSelectedFile(file);
        setSearchTerm(file.MatterNumber);
        setShowDropdown(false);
    };

    useEffect(() => {
        if (showAddModal) {
            setAppTitle("");
            setAppDetails("");
            setAppDate("");
            setAppTime("");
            setHour("");
            setMinute("00");
            setAmPm("AM");
            //setLinkToCurrentFile(false); Link to current file code
            setValidationError(null);
        }
    }, [showAddModal]);

    useEffect(() => {
        setAppTime(formatTimeString());
    }, [hour, minute, amPm]);

    const handleSave = async () => {
        if (!appTitle.trim()) {
            setValidationError("Appointment title is required");
            return;
        }

        if (!appDate) {
            setValidationError("Appointment date is required");
            return;
        }
        if (!appTime) {
            setValidationError("Appointment time is required");
            return;
        }
    
        let linkToFile = null;
        let linkToFileMatter = "";
        let recordID: number | undefined = undefined;
        let linkID: string | undefined = undefined;
        let tableLinkedToID: string | undefined = undefined;
        
        if (!isFromCalendar) {
        //if (linkToCurrentFile) { Link to current file code
            const fileInfo = getCurrentFileInfo();
            if (fileInfo.appId) {
                linkToFile = fileInfo.appId || null;
                linkToFileMatter = fileInfo.matterNumber || "";
                if (linkToFile) {
                    recordID = Number(linkToFile);
                    linkID = 'AppID';
                    tableLinkedToID = 'TPAppXML';
                }
            }
        }else{
            if (selectedFile) {
                linkToFile = selectedFile.appId || null;
                linkToFileMatter = selectedFile.MatterNumber || "";
                if (linkToFile) {
                    recordID = Number(linkToFile);
                    linkID = 'AppID';
                    tableLinkedToID = 'TPAppXML';
                }
            }
        }

        const ownerId = SESSION_INFO?.firm?.id?.toString();
        const newTime = formatTimeString();
        const newShortDescr = buildNewShortDescr(appTitle.trim(), newTime, linkToFileMatter || undefined);
        const newLongDescr = buildNewLongDescr(appTitle.trim(), newTime, linkToFileMatter || undefined);
        
        let xmldata: string;
        try {
            const escapeXmlAttr = (str: string) => {
                if (str == null) return '';
                str = String(str);
                return str
                    .replace(/&/g, '&amp;')
                    .replace(/"/g, '&quot;')
                    .replace(/'/g, '&apos;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;');
            };
            const paddedHour = hour.padStart(2, '0');
            
            let xmlAttributes = `calendarItemTimeMin="${minute.padStart(2, '0')}" calendarItemTimeAmPm="${amPm.toUpperCase()}" shortDescrtmp="${escapeXmlAttr(appTitle.trim())}" calendarItemTimeHour="${paddedHour}"`;
            
            if (linkToFile) {
                xmlAttributes += ` LinkToFile="${escapeXmlAttr(linkToFile)}"`;
            }
            if (linkToFileMatter) {
                xmlAttributes += ` LinkToFileMatter="${escapeXmlAttr(linkToFileMatter)}"`;
            }
            //if (linkToCurrentFile) { Link to current file code
            if (linkToFile) {
                xmlAttributes += ` LinkToCurrent="1"`;
            }
            
            xmldata = `<calendar ${xmlAttributes}/>`;
        } catch (error) {
            Logger.error('Error creating XML:', error);
            const paddedHour = hour.padStart(2, '0');
            let xmlAttributes = `calendarItemTimeMin="${minute.padStart(2, '0')}" calendarItemTimeAmPm="${amPm.toUpperCase()}" shortDescrtmp="${appTitle.trim()}" calendarItemTimeHour="${paddedHour}"`;
            if (linkToFile) {
                xmlAttributes += ` LinkToFile="${linkToFile}"`;
            }
            if (linkToFileMatter) {
                xmlAttributes += ` LinkToFileMatter="${linkToFileMatter}"`;
            }
            //if (linkToCurrentFile) {
            if (linkToFile) {
                xmlAttributes += ` LinkToCurrent="1"`;
            }
            xmldata = `<calendar ${xmlAttributes}/>`;
        }

        const newAppointment = {
            typeID: 0,
            shortDescr: newShortDescr,
            longDescr: newLongDescr,
            details: appDetails,
            calendarItemDate: appDate,
            calendarItemTime: newTime,
            privateItem: "0",
            xmldata: xmldata,
            LinkToFile: linkToFile ?? null,
            LinkToFileMatter: linkToFileMatter ?? null,
            recordID: recordID ?? null,
            linkID: linkID ?? null,
            tableLinkedToID: tableLinkedToID ?? null,
            ownerID: ownerId ?? null
        };

        try {
            await handleAddAppointment(newAppointment);
            const appointmentCopy = { ...newAppointment };
            dispatch(setCalendarAppointment(appointmentCopy));
            displayToastMsg({
                id: Date.now(),
                type: "success",
                msg: "Appointment added."
            }, null);
            setSelectedFile(null);
            setSearchTerm("");
            setShowAddModal(false);
        } catch (error) {
            Logger.error('Error adding appointment:', error);
            displayToastMsg({
                id: Date.now(),
                type: "error",
                msg: "Failed to add appointment"
            }, null);
        }
    };
    
    //code for link to current file
    const hasCurrentFile = () => {
        const fileInfo = getCurrentFileInfo();
        return !!fileInfo.appId;
    };

    return (
        <Modal size="sm" className="modal-sm-long" centered show={showAddModal} onHide={() => {
            setValidationError(null);
            setShowAddModal(false);
            setSelectedFile(null);
            setSearchTerm("");
        }}>
            <Modal.Header closeButton={false}>
                <h2>Add Appointment</h2>
            </Modal.Header>
            <Modal.Body>
                {validationError && (
                    <Alert variant="danger" className="mb-3">
                        {validationError}
                    </Alert>
                )}
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>Appointment Title</Form.Label>
                        <Form.Control 
                            type="text" 
                            value={appTitle}
                            onChange={(e) => setAppTitle(e.target.value)}
                            placeholder="Enter appointment title"
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Details</Form.Label>
                        <Form.Control 
                            as="textarea" 
                            rows={3}
                            value={appDetails}
                            onChange={(e) => setAppDetails(e.target.value)}
                            placeholder="Enter appointment details"
                        />
                    </Form.Group>
                    {isFromCalendar && (
                        <Form.Group className="mb-3 position-relative">
                        <Form.Label>Link to File</Form.Label>
                        <InputGroup>
                            <InputGroup.Text className="bg-transparent border-end-0">
                                <i className="fa-duotone fa-magnifying-glass"></i>
                            </InputGroup.Text>
                            <Form.Control
                                type="text"
                                placeholder="Search file # "
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setShowDropdown(true);
                                    setSelectedFile(null); 
                                }}
                                onFocus={() => {
                                    setShowDropdown(true);
                                    fetchFirmFiles();
                                }}
                                onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                                className="border-start-0 shadow-none"
                            />
                        </InputGroup>
                        
                        {showDropdown && (
                            <div className="dropdown-menu show w-100 p-0" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                {isLoadingFiles ? (
                                    <div className="dropdown-item-text text-muted text-center py-2">Loading...</div>
                                ) : (
                                    <>
                                        {filtteredFiles.length > 0 ? (
                                            filtteredFiles.map((file: any) => (
                                                <button
                                                    key={file.appId}
                                                    type="button"
                                                    className="dropdown-item py-2"
                                                    onClick={() => handleFileSelect(file)}
                                                >
                                                    <div className="fw-bold">{file.MatterNumber}</div>
                                                </button>
                                            ))
                                        ) : (
                                            <div className="dropdown-item-text text-muted text-center py-2">No files found</div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </Form.Group>
                        )}
                    <Form.Group className="mb-3">
                        <div className="d-flex justify-content-between align-items-center gap-3">
                            {/* Date Picker */}
                            <div style={{ flex: 1 }}>
                                <Form.Label>Date</Form.Label>
                                <DatePicker
                                    date={appDate}
                                    setDate={(newDate) => setAppDate(newDate)}
                                    placeholder="Select date"
                                    popperPlacement="top-start"
                                    portalId="root"
                                />
                            </div>
                            {/* Time picker */}
                            <div style={{ flex: 1 }}>
                                <Form.Label>Time</Form.Label>
                                <div className="d-flex gap-2 align-items-center">
                                    <Form.Control 
                                        type="number" 
                                        min="1" 
                                        max="12"
                                        value={hour}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (value === '' || (parseInt(value) >= 1 && parseInt(value) <= 12)) {
                                                setHour(value);
                                            }
                                        }}
                                        onBlur={(e) => {
                                            const value = parseInt(e.target.value);
                                            if (value < 1) setHour('1');
                                            if (value > 12) setHour('12');
                                        }}
                                        placeholder="Hour"
                                        style={{ flex: 1, minWidth: 80 }}
                                    />
                                    <span>:</span>
                                    <Form.Select
                                        value={minute}
                                        onChange={(e) => setMinute(e.target.value)}
                                        style={{ flex: 1, minWidth: 80 }}
                                    >
                                        <option value="00">00</option>
                                        <option value="15">15</option>
                                        <option value="30">30</option>
                                        <option value="45">45</option>
                                    </Form.Select>
                                    <Form.Select 
                                        value={amPm}
                                        onChange={(e) => setAmPm(e.target.value)}
                                        style={{ flex: 1, minWidth: 80 }}
                                    >
                                        <option value="AM">AM</option>
                                        <option value="PM">PM</option>
                                    </Form.Select>
                                </div>
                            </div>
                        </div>
                    </Form.Group>
                    {/* <Form.Group className="mb-3">
                        <FormCheck
                            type="checkbox"
                            id="linkToCurrentFile"
                            label="Link to current file"
                            checked={linkToCurrentFile}
                            onChange={(e) => setLinkToCurrentFile(e.target.checked)}
                            disabled={!hasCurrentFile()}
                            title={hasCurrentFile() ? "Select this checkbox if you want this appointment to link to this particular application" : "No file is currently open"}
                        />
                    </Form.Group> */}
                </Form>
            </Modal.Body>
            <Modal.Footer className="justify-content-between">
                <Button variant="text" onClick={() => {
                    setValidationError(null);
                    setShowAddModal(false);
                }}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSave}>
                    Add
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AddNewAppModal;